import { use, useCallback, useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import styled from 'styled-components';
import { Copyright, defaultPlaylistName } from '../../../common/src/model.ts';
import PauseIcon from '../assets/pause.svg?react';
import PlayIcon from '../assets/play.svg?react';
import { LocalStorageKey } from '../data/client_local_storage.ts';
import { useDisplayHymnal } from '../data/use_hymnals.tsx';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { isCustomSong } from '../util/path.ts';
import { HymnalIcon } from '../util/shared.tsx';
import { locationSliderUpdateIntervalMs, swipeDeltas, swipeDurationMs } from './player_util.tsx';
import { PracticeVideoContext } from './practice_video/practice_video_context.tsx';
import { SequencerContext } from './sequencer.tsx';

const hymnalIconSize = '45px';
/* plain Spotify-style glyphs (issue #3390); pause is optically larger than play, and the
   paddings keep both buttons the same 58x50px clickable box so nothing shifts when toggling.
   The right padding gives extra clickable breathing room between the glyph and the bar edge.
   The wrappers use content-box so these sizes are the true glyph sizes; the padding only
   extends the clickable area (the app default border-box would shrink the glyph instead). */
const playGlyphSize = '28px';
const playGlyphPadding = '11px';
const playGlyphRightPadding = '13px';
const pauseGlyphSize = '30px';
const pauseGlyphPadding = '10px';
const pauseGlyphRightPadding = '12px';
const horizontalMargin = '10px';

export function MiniPlayer() {
  const isMiniPlayerVisible = useIsMiniPlayerVisible();
  const [playlist] = useLocalStorage(LocalStorageKey.Playlist);
  const showPlaylistName = playlist?.name && playlist.name !== defaultPlaylistName;
  const {
    hymn,
    copyrightIssues,
    nextSong,
    previousSong,
    playing,
    sequencer,
    getCurrentPercent,
    hasVocals,
    hasMidi,
    videoEnabled,
  } = use(SequencerContext);
  const { hasPracticeVideos } = use(PracticeVideoContext);
  const hasAudio = hasVocals || hasMidi || hasPracticeVideos;
  // the title the org selected for the hymn's hymnal, e.g. an alternate title (issue #3454)
  const hymnal = useDisplayHymnal(hymn);
  const [_isPlayerMaximized, setIsPlayerMaximized] = useLocalStorage(LocalStorageKey.IsPlayerMaximized);
  const [currentPercent, setCurrentPercent] = useState(() => getCurrentPercent());

  const onMaximize = useCallback(() => {
    setIsPlayerMaximized(true);
  }, [setIsPlayerMaximized]);

  const onClickPlayPause = useCallback(() => {
    if (playing) {
      sequencer.pause();
      return;
    }
    // YouTube's terms: a practice video plays only with its picture in view, so the player opens first
    // (GitHub issue #4041)
    if (videoEnabled) {
      setIsPlayerMaximized(true);
    }
    sequencer.play();
  }, [playing, sequencer, setIsPlayerMaximized, videoEnabled]);

  useEffect(() => {
    const interval = window.setInterval(() => setCurrentPercent(getCurrentPercent()), locationSliderUpdateIntervalMs);
    return () => window.clearInterval(interval);
  }, [getCurrentPercent]);

  const swipeHandler = useSwipeable({
    delta: swipeDeltas,
    swipeDuration: swipeDurationMs,
    onSwipedLeft: nextSong,
    onSwipedRight: previousSong,
  });

  return !isMiniPlayerVisible ? null : (
    <OuterWrapper>
      <MiniPlayerWrapper $isVisible={isMiniPlayerVisible} {...swipeHandler}>
        {!hymnal?.icon || !hymn?.slug || !hymn.hymnal || isCustomSong(hymn.slug) ? null : (
          <HymnalIconWrapper onClick={onMaximize}>
            <HymnalIcon $hasBorder={!!hymnal.icon.hasBorder} $hasWash={false} src={hymnal.icon.path} />
          </HymnalIconWrapper>
        )}
        <ContentStacker onClick={onMaximize}>
          <SongName>{hymn?.title}</SongName>
          <PlaylistName>{showPlaylistName ? playlist?.name : (hymnal?.name ?? hymn?.hymnal)}</PlaylistName>
        </ContentStacker>
        {!copyrightIssues?.includes(Copyright.Music) &&
          hasAudio &&
          (playing ? <PauseIconWrapper onClick={onClickPlayPause} /> : <PlayIconWrapper onClick={onClickPlayPause} />)}
      </MiniPlayerWrapper>
      <LocationBackground>
        <LocationFill style={{ width: `${currentPercent}%` }} />
      </LocationBackground>
    </OuterWrapper>
  );
}

export function useIsMiniPlayerVisible(): boolean {
  const [isPlayerMaximized] = useLocalStorage(LocalStorageKey.IsPlayerMaximized);
  const [playlist] = useLocalStorage(LocalStorageKey.Playlist);
  const { hymn } = use(SequencerContext);
  return !!playlist && !isPlayerMaximized && !!hymn;
}

const OuterWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const MiniPlayerWrapper = styled.div<{ $isVisible: boolean }>`
  display: ${(props) => (props.$isVisible ? 'flex' : 'none')};
  align-items: center;
  padding: 5px 5px;
  /* 100% (not 100vw) so the mini player also fits a narrower container, e.g. in Storybook */
  width: calc(100% - ${horizontalMargin} * 2);
  margin: 0 ${horizontalMargin};
  background-color: var(--color-background-minimized-player);
  border-radius: 10px;
  font-size: 0.9rem;
  cursor: pointer;
`;

const ContentStacker = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  text-wrap: nowrap;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  margin-left: 5px;
`;

const SongName = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
`;

const PlaylistName = styled.div`
  color: var(--color-text-light);
`;

/* plain glyph in text color, a la Spotify — no purple disc (issue #3390) */
const PlayIconWrapper = styled(PlayIcon)`
  box-sizing: content-box;
  width: ${playGlyphSize};
  height: ${playGlyphSize};
  padding: ${playGlyphPadding} ${playGlyphRightPadding} ${playGlyphPadding} ${playGlyphPadding};
  fill: var(--color-text);
  flex-shrink: 0;
`;

const PauseIconWrapper = styled(PauseIcon)`
  box-sizing: content-box;
  width: ${pauseGlyphSize};
  height: ${pauseGlyphSize};
  padding: ${pauseGlyphPadding} ${pauseGlyphRightPadding} ${pauseGlyphPadding} ${pauseGlyphPadding};
  fill: var(--color-text);
  flex-shrink: 0;
`;

const HymnalIconWrapper = styled.div`
  width: ${hymnalIconSize};
  height: ${hymnalIconSize};
  margin-right: 5px;
`;

const LocationBackground = styled.div`
  display: flex;
  justify-content: left;
  bottom: 0;
  position: absolute;
  width: calc(100% - 40px);
  height: 2px;
  background-color: var(--color-mini-player-progress-track);
  border-radius: 10px;
`;

const LocationFill = styled.div`
  height: 100%;
  background-color: var(--color-mini-player-progress);
`;
